<script setup>
// Imports
import { ref, onMounted } from 'vue';
import { Cropper, CircleStencil } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import axios from 'axios';
import { useStorage } from '@/composables/use-storage';
// Components and composables
import Toast from '@/components/toast.vue';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';
import { useToast } from '@/composables/use-toast';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/store/UserStore';
import postalCodePatterns from '@/data/postalCodePatterns.json';
import ChangeGroupForm from './ChangeGroupForm.vue';
import { fetchAvailableCountries } from './services/registerFlowService';

const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const { saveAuthToken, saveUserInfo, verifyAuthToken, verifyUserInfo, hasFlag } = useStorage();
const userData = route.state;

const accessCodeData = ref(null);

onMounted(async () => {
    accessCodeData.value = userStore.getAccessCodeResponse;
    fetchCountries();
    if (!accessCodeData.value && !userData) {
        router.push('/register');
    }
    // Fetch onboarding fields if access code exists
    if (accessCodeData.value?.accessCode) {
        await fetchOnboardingFields();
    }
});

const groupHasFlag = (flag) => {
    return accessCodeData.value?.flags.includes(flag);
}

const { TextInput, PrimaryButton, ButtonLoading,  SelectInput} = useLayoutComponents();
const { User } = useIcons();
const { addToast } = useToast();

const showCropper = ref(false);
const imageSource = ref('');
const cropperResult = ref(null);
const profilePicture = localStorage.getItem('profilePicture');
const profileImagePreview = ref('');
const showPreview = ref(false);
const showDefaultAvatar = ref(false);

const usernameError = ref('');
const nameError = ref('');
const passwordError = ref('');
const passwordConfirmError = ref('');
const loading = ref(false);
const postalCodeError = ref('');
const countries = ref([]);
const countrySelected = ref('');

const fetchCountries = async () => {
    const availableCountries = await fetchAvailableCountries();
    countries.value = availableCountries.map(country => ({
        label: country.name.common,
        value: country.cca2
    }));
}

const validatePostalCode = () => {
    const postalCode = document.getElementById('postal_code').value;
    const selectedCountry = countrySelected.value;
    postalCodeError.value = '';

    if (!postalCode) {
        postalCodeError.value = 'Postal code is required';
        return;
    }

    if (!selectedCountry) {
        postalCodeError.value = 'Please select a country first';
        return;
    }

    const pattern = postalCodePatterns[selectedCountry];
    if (pattern) {
        const regex = new RegExp('^' + pattern + '$');
        if (!regex.test(postalCode)) {
            postalCodeError.value = 'Invalid postal code format for selected country';
        }
    }
}

const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
        if (file.size > 2 * 1024 * 1024) {
            addToast('File size must be less than 2MB', 'error');
            return;
        }
        showDefaultAvatar.value = false;

        const reader = new FileReader();
        reader.onload = (e) => {
            imageSource.value = e.target.result;
            showCropper.value = true;
        };
        reader.readAsDataURL(file);
    }
};

const onCropperChange = ({ canvas }) => {
    cropperResult.value = canvas;
};

const saveCroppedImage = () => {
    if (cropperResult.value) {
        const maxSize = 200;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const ratio = Math.min(maxSize / cropperResult.value.width, maxSize / cropperResult.value.height);

        canvas.width = cropperResult.value.width * ratio;
        canvas.height = cropperResult.value.height * ratio;

        ctx.drawImage(cropperResult.value, 0, 0, canvas.width, canvas.height);

        const base64Image = canvas.toDataURL('image/jpeg', 0.6);

        profileImagePreview.value = base64Image;
        showPreview.value = true;
        showDefaultAvatar.value = false;
        showCropper.value = false;
    }
};

const cancelCropping = () => {
    showCropper.value = false;
    const fileInput = document.getElementById('profile_image');
    if (fileInput) fileInput.value = '';
};

const removeImage = () => {
    profileImagePreview.value = '';
    showPreview.value = false;
    showDefaultAvatar.value = true;
    const fileInput = document.getElementById('profile_image');
    if (fileInput) fileInput.value = '';
};

const validateNameInput = () => {
    const name = document.getElementById('name').value;
    nameError.value = '';

    if (!name) {
        nameError.value = 'Name is required';
    }

};

const validateUsernameInput = (showToast = false) => {
    const username = document.getElementById('username').value;
    usernameError.value = '';

    if (!username) {
        usernameError.value = 'Username is required';
        return;
    }
    if (username.length < 3 || username.length > 20) {
        usernameError.value = 'Username must be between 3 and 20 characters';
        if (showToast) addToast('Username must be between 3 and 20 characters', 'error');
    }
    if (!/^[a-zA-Z0-9]\w*[a-zA-Z0-9]$/.test(username)) {
        usernameError.value = 'Username format is invalid';
        if (showToast) addToast('Username can only contain letters, numbers, and underscores (not at start/end)', 'error');
    }
};

const validatePassword = (showToast = false) => {
    const password = document.getElementById('password').value;
    passwordError.value = '';

    if (!password) {
        passwordError.value = 'Password is required';
        return;
    }

    if (password.length < 8) {
        passwordError.value = 'Password must be at least 8 characters';
        if (showToast) addToast('Password must be at least 8 characters', 'error');
    }
};

const validatePasswordConfirm = (showToast = false) => {
    const password = document.getElementById('password').value;
    const passwordConfirm = document.getElementById('password_confirmation').value;
    passwordConfirmError.value = '';

    if (!passwordConfirm) {
        passwordConfirmError.value = 'Password confirmation is required';
        return;
    }

    if (password !== passwordConfirm) {
        passwordConfirmError.value = 'Passwords do not match';
        if (showToast) addToast('Passwords do not match', 'error');
    }
};

const toggleElementErrorClass = (element, hasError) => {
    if (!element) return;
    if (hasError) {
        element.classList.add('border-red-500');
    } else {
        element.classList.remove('border-red-500');
    }
};

const validateCountryField = () => {
    const countryContainer = document.querySelector('[data-testid="country-input"]')?.closest('.select');
    const countrySelectElement = countryContainer?.querySelector('.select__select');
    const hasError = !countrySelected.value;
    toggleElementErrorClass(countrySelectElement, hasError);
    return hasError;
};

const validateRegularField = (fieldId) => {
    const element = document.getElementById(fieldId);
    if (!element) {
        console.error(`Element with id ${fieldId} not found`);
        return false;
    }
    const hasError = !element.value;
    toggleElementErrorClass(element, hasError);
    return hasError;
};

const validateFields = () => {
    const baseRequiredFields = ['username', 'name', 'password', 'password_confirmation'];
    if (groupHasFlag('stripe-payment')) {   
        baseRequiredFields.push('postal_code', 'country');
    }
    const requiredFields = baseRequiredFields;
    
    let missingRequiredFields = false;
    
    for (const field of requiredFields) {
        if (field === 'country') {
            missingRequiredFields = validateCountryField() || missingRequiredFields;
            continue;
        }
        missingRequiredFields = validateRegularField(field) || missingRequiredFields;
    }
       
    // Validate custom onboarding fields
    if (onboardingFields.value && onboardingFields.value.length > 0) {
        onboardingFields.value.forEach(field => {
            const fieldKey = typeof field === 'string' ? field : field.field || field.name || field;
            const isRequired = typeof field === 'object' ? field.required !== false : true;
            const fieldId = `onboarding_${fieldKey}`;
            const element = document.getElementById(fieldId);
            
            if (!element) {
                console.error(`Element with id ${fieldId} not found`);
                return;
            }
            
            const fieldValue = onboardingFieldsData.value[fieldKey];
            
            if (isRequired && !fieldValue) {
                element.classList.add('border-red-500');
                missingRequiredFields = true;
            } else {
                element.classList.remove('border-red-500');
            }
        });
    }
    
    if (missingRequiredFields) {
        addToast('Please fill in all required fields', 'error');
        return false;
    }

    validateUsernameInput(true);
    validateNameInput();
    validatePassword(true);
    validatePasswordConfirm(true);
    if (groupHasFlag('stripe-payment')) {
        validatePostalCode();
    }
    
    if (onboardingFields.value && onboardingFields.value.length > 0) {
        onboardingFields.value.forEach(field => {
            const fieldKey = typeof field === 'string' ? field : field.field || field.name || field;
            validateOnboardingField(fieldKey, true);
        });
    }

    const hasOnboardingErrors = Object.values(onboardingFieldsErrors.value).some(error => error !== '');
    return !(usernameError.value || nameError.value || passwordError.value || passwordConfirmError.value || postalCodeError.value || hasOnboardingErrors);
};

const buildRequestData = () => {
    const data = {
        username: document.getElementById('username').value,
        name: document.getElementById('name').value,
        password: document.getElementById('password').value,
        password_confirmation: document.getElementById('password_confirmation').value,
        email: accessCodeData.value?.email || userData?.email,
        account_type: accessCodeData.value?.accountType,
        access_code: accessCodeData.value?.accessCode,
        profile_pic: profileImagePreview.value || null,
        seller_whitelist: accessCodeData.value?.accountType === 'seller',
        country: groupHasFlag('stripe-payment') ? countrySelected.value : null,
        postal_code: groupHasFlag('stripe-payment') ? document.getElementById('postal_code').value : null,
        status: 'active',
        additional_form: additionalFields.value
    };

    if (accessCodeData.value?.accessCode) {
        if (onboardingFields.value && onboardingFields.value.length > 0) {
            const onboardingData = {};
            onboardingFields.value.forEach(field => {
                const fieldKey = typeof field === 'string' ? field : field.field || field.name || field;
                const fieldValue = onboardingFieldsData.value[fieldKey];
                
                const labelKey = getFieldLabelForRequest(field);
            
                onboardingData[labelKey] = fieldValue || '';
            });
            data.onboarding_fields = onboardingData;
        } 
    } 
    return data;
};

const handleSuccessfulResponse = async (response) => {
    if (response.data.token) {
       await saveAuthToken(response.data.token);
    }
    
    if (accessCodeData.value?.accountType === 'seller' && response.data.userInfos.seller) {
        response.data.userInfos.seller.payout_address = document.getElementById('payout_address')?.value || null;
    }
    
    await saveUserInfo(response.data.userInfos);

    if (profileImagePreview.value) {
        localStorage.setItem('profilePicture', profileImagePreview.value);
    }

    if (!verifyAuthToken() || !verifyUserInfo()) {
        throw new Error('Failed to store authentication data');
    }

    const accountType = userStore.getAccessCodeResponse?.accountType;
    const sellerRoute = { name: 'creator-profile', query: { page: 'eval-builder', subpage: 'eval-builder' } };
    const routes = {
        seller: sellerRoute,
        buyer: { path: '/explore', query: { 'buy-credits': '1' } },
        default: '/creator-profile-edit'
    };
    
    router.push(routes[accountType] || routes.default);
};

const sendRequest = async () => {
    usernameError.value = '';
    nameError.value = '';
    passwordError.value = '';
    passwordConfirmError.value = '';
    onboardingFieldsErrors.value = {};
    if (!validateFields()) return;

    loading.value = true;

    try {
        const response = await axios.post('/api/users', buildRequestData(), {
            headers: { 'Content-Type': 'application/json' }
        });
        if (response.data.statusCode === 200 || response.data.statusCode === 201) {
            addToast('Account created. Please check your email to verify your address.', 'success');
            await handleSuccessfulResponse(response);
        } else if (response.data.statusCode === 409) {
            addToast('Username already exists', 'error');
        } else {
            throw new Error(response.data.message);
        }
    } catch (error) {
        console.error(error);
        addToast(error.response?.data?.message, 'error');
    } finally {
        loading.value = false;
    }
};

const cropperRef = ref(null);
const additionalFields = ref(null);
const onboardingFieldsData = ref({});
const onboardingFieldsErrors = ref({});
const onboardingFields = ref([]);

const fetchOnboardingFields = async () => {
    if (accessCodeData.value?.accessCode) {
        try {
            const response = await axios.post('/api/users/validate-access-code', {
                email: accessCodeData.value.email,
                access_code: accessCodeData.value.accessCode
            });
            if (response.data.statusCode === 200 && response.data.onboardingFields) {
                onboardingFields.value = response.data.onboardingFields;
            }
        } catch (error) {
            console.error('Error fetching onboarding fields:', error);
        }
    }
};

const validateChangeGroupForm = (formData) => {
    if(formData.needsRFP === 'yes' || formData.wantToSell === 'yes') {
        additionalFields.value = formData
    } else {
        additionalFields.value = null;
    }
}

const findOnboardingField = (fieldName) => {
    return onboardingFields.value.find(f => {
        if (typeof f === 'string') return f === fieldName;
        return f.field === fieldName || f.name === fieldName;
    });
};

const getFieldKey = (field) => {
    return typeof field === 'string' ? field : field.field || field.name || field;
};

const getFieldLabelForError = (field, fieldKey) => {
    return typeof field === 'object' ? (field.label || field.field || field.name || fieldKey) : fieldKey;
};

const validateRequiredField = (fieldKey, field, showToast) => {
    const label = getFieldLabelForError(field, fieldKey);
    onboardingFieldsErrors.value[fieldKey] = `${label} is required`;
    if (showToast) addToast(`${label} is required`, 'error');
};

const validateNumber = (value) => {
    const numValue = value.trim();
    return (numValue !== '' && !isNaN(numValue) && isFinite(numValue)) 
        ? '' 
        : 'Please enter a valid number';
};

const validateFieldType = (fieldValue, fieldType) => {
    if (!fieldValue || !fieldType) return '';
    
    if (fieldType === 'number') {
        return validateNumber(fieldValue);
    }
    
    return '';
};

const validateOnboardingField = (fieldName, showToast = false) => {
    const field = findOnboardingField(fieldName);
    if (!field) return;
    
    const fieldKey = getFieldKey(field);
    const fieldValue = onboardingFieldsData.value[fieldKey];
    const isRequired = isFieldRequired(field);
    const fieldType = getFieldType(field);
    
    onboardingFieldsErrors.value[fieldKey] = '';
    
    if (isRequired && !fieldValue) {
        validateRequiredField(fieldKey, field, showToast);
        return;
    }
    
    const typeError = validateFieldType(fieldValue, fieldType);
    if (typeError) {
        onboardingFieldsErrors.value[fieldKey] = typeError;
        if (showToast) addToast(typeError, 'error');
    }
}

const getFieldLabel = (field) => {
    if (typeof field === 'string') {
        return field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
    }
    return field.label || field.field || field.name || field;
}

const getFieldType = (field) => {
    if (typeof field === 'string') return 'text';
    return field.type || 'text';
}

const getFieldPlaceholder = (field) => {
    if (typeof field === 'string') {
        return `Enter ${field.replace(/_/g, ' ')}`;
    }
    return field.placeholder || `Enter ${getFieldLabel(field)}`;
}

const isFieldRequired = (field) => {
    if (typeof field === 'string') return true;
    return field.required !== false;
}

const getFieldLabelForRequest = (field) => {
    const label = getFieldLabel(field);
    const required = isFieldRequired(field);
    return required ? `${label}` : label;
}

const handleOnboardingFieldInput = (field, value) => {
    const fieldKey = typeof field === 'string' ? field : field.field || field.name || field;
    onboardingFieldsData.value[fieldKey] = value || '';
    validateOnboardingField(fieldKey, false);
}
</script>
<template>
  
    <div class="min-h-screen flex items-center justify-center bg-gradient-to-b from-echoes-background-gray to-echoes-background-black">
        <div class="w-full">
             <div class="flex flex-col items-center justify-start md:flex-row  my-8">
                    <div class="flex w-fit flex-col justify-center mx-auto">
                        <h1
                            class="text-xl md:text-2xl 2xl:text-4xl font-bold text-center mx-auto md:text-left bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent"
                            data-testid="account-creation-title">
                            Let's create your account!
                        </h1>
                        <p class="text-white/80 text-center md:text-left  font-medium  text-sm 2xl:text-lg">
                            It will only take a minute to complete your registration.
                        </p>
                    </div>
                </div>
            <div
                class="bg-echoes-background-black rounded-2xl border border-echoes-border-gray mb-4 px-6 py-6 max-w-xl 2xl:max-w-2xl mx-auto">
                <div class="flex flex-col md:flex-row items-center justify-between rounded-xl mb-6 2xl:mb-8">
                    <div class="flex flex-col md:flex-row items-center gap-6">
                        <div class="profile-photo">
                            <div class="profile-image-preview"
                                :class="{ 'has-image': profileImagePreview || (profilePicture && !showDefaultAvatar) }">
                                <img alt="profile preview" v-if="profilePicture && !showPreview && !showDefaultAvatar" :src="profilePicture"
                                    class="preview-image">
                                <img alt="selected profile preview" v-else-if="profileImagePreview && showPreview && !showDefaultAvatar"
                                    :src="profileImagePreview" class="preview-image">
                                <User v-else class="default-avatar" />
                            </div>
                        </div>
                        
                        <div class="flex flex-col justify-center items-start mt-4 md:mt-0">
                            <label for="profile_image" class="text-white font-medium text-sm 2xl:text-base mb-1">Profile Picture<span class="text-white/80 text-sm 2xl:text-base font-semibold ml-2">(optional)</span></label>
                            <p class="text-white/80 text-xs 2xl:text-sm mb-2">Please upload a profile picture <br>(max 2MB)</p>
                        </div>
                    </div>
                    
                    <div class="flex gap-2 mt-4 md:mt-0">
                        <div class="visually-hidden">
                            <label for="profile_image">Upload profile image</label>
                            <input type="file" id="profile_image" accept="image/*" @change="handleImageUpload" data-testid="profile-image-input" />
                        </div>
                        <button 
                            type="button" 
                            class="btn-image-action bg-echoes-primary hover:bg-echoes-primary/50" 
                            @click="$refs.profileImageInput.click()" 
                            data-testid="upload-image-button">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 h-5 mr-2">
                                <path fill="currentColor"
                                    d="M19,4H5A3,3,0,0,0,2,7V17a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V7A3,3,0,0,0,19,4ZM5,18a1,1,0,0,1-1-1V14.58l3.3-3.29a1,1,0,0,1,1.4,0L15.41,18Zm15-1a1,1,0,0,1-1,1h-.77l-3.81-3.83.88-.88a1,1,0,0,1,1.4,0L20,16.58Zm0-3.24-1.88-1.87a3.06,3.06,0,0,0-4.24,0l-.88.88L10.12,9.89a3.06,3.06,0,0,0-4.24,0L4,11.76V7A1,1,0,0,1,5,6H19a1,1,0,0,1,1,1Z">
                                </path>
                            </svg>
                            Upload Image
                        </button>
                        <input ref="profileImageInput" type="file" accept="image/*" @change="handleImageUpload" class="hidden" />
                        <button v-if="profileImagePreview && !showDefaultAvatar" type="button"
                            class="btn-image-action bg-red-500 hover:bg-red-600" @click="removeImage">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 h-5">
                                <path fill="currentColor"
                                    d="M20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Z">
                                </path>
                            </svg>
                            
                        </button>
                    </div>
                </div>
                <div class="space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label for="username" class="block text-white text-sm 2xl:text-base mb-2">Username</label>
                            <div class="relative">
                                <TextInput type="text" id="username" placeholder="Username"
                                    @input="validateUsernameInput(false)" class="w-full" 
                                    dataTestId="username-input" />
                            </div>
                        </div>

                        <div>
                            <label for="name" class="block text-white text-sm 2xl:text-base mb-2">Name</label>
                            <div class="relative">
                                <TextInput type="text" id="name"
                                    placeholder="Full name or organization name" class="w-full"
                                    @input="validateNameInput" 
                                    dataTestId="name-input" />
                            </div>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4" v-if="groupHasFlag('stripe-payment')">
                        <div>
                            <label for="country" class="block text-white text-sm 2xl:text-base mb-2">Country</label>
                            <div class="relative">
                                <SelectInput
                                    v-model="countrySelected"
                                    :options="countries" 
                                    id="country"
                                    placeholder="Select country" 
                                    class="w-full"
                                    dataTestId="country-input"
                                ></SelectInput>
                            </div>
                        </div>

                        <div>
                            <label for="postal_code" class="block text-white text-sm 2xl:text-base mb-2">Postal Code</label>
                            <div class="relative">
                                <TextInput 
                                    type="text" 
                                    id="postal_code" 
                                    placeholder="Postal Code" 
                                    class="w-full" 
                                    @input="validatePostalCode()" 
                                    :disabled="!countrySelected" 
                                    dataTestId="postal-code-input"/>
                            </div>
                            <p v-if="postalCodeError" class="text-red-500 text-xs 2xl:text-sm mt-1">{{ postalCodeError }}</p>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label for="password" class="block text-white text-sm 2xl:text-base mb-2">Password</label>
                            <div class="relative">
                                <TextInput id="password" type="password"
                                    placeholder="Min. 8 characters" class="w-full"
                                    @input="validatePassword(false)" 
                                    dataTestId="password-input" />
                            </div>
                        </div>

                        <div>
                            <label for="password_confirmation" class="block text-white text-sm 2xl:text-base mb-2">Confirm Password</label>
                            <div class="relative">
                                <TextInput id="password_confirmation" type="password"
                                    placeholder="Confirm your password" class="w-full"
                                    @input="validatePasswordConfirm(false)" 
                                    dataTestId="password-confirmation-input" />
                            </div>
                        </div>
                    </div>

                    <!-- Render onboarding fields dynamically when access code is defined -->
                    <div v-if="accessCodeData?.accessCode && onboardingFields && onboardingFields.length > 0">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div v-for="field in onboardingFields" :key="typeof field === 'string' ? field : field.field || field.name || field">
                                <label 
                                    :for="`onboarding_${typeof field === 'string' ? field : field.field || field.name || field}`" 
                                    class="block text-white text-sm 2xl:text-base mb-2">
                                    {{ getFieldLabel(field) }}
                                </label>
                                <div class="relative">
                                    <TextInput 
                                        :type="getFieldType(field)" 
                                        :id="`onboarding_${typeof field === 'string' ? field : field.field || field.name || field}`"
                                        :placeholder="getFieldPlaceholder(field)" 
                                        class="w-full"
                                        @update:model-value="(value) => handleOnboardingFieldInput(field, value)"
                                        :dataTestId="`onboarding-${typeof field === 'string' ? field : field.field || field.name || field}-input`" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="accessCodeData?.accessCode === 'new-user'">
                        <ChangeGroupForm :key="'change-group-form'" @change="validateChangeGroupForm" />
                    </div>

                    <div>
                        <button @click="sendRequest" :disabled="loading"
                            class="mt-4 bg-gradient-to-br from-green-500 to-green-700 hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 text-white font-semibold py-2.5 2xl:py-3.5 w-full rounded-xl text-sm"
                            data-testid="register-button">
                            <ButtonLoading v-if="loading" size="sm" text="Registering..." />
                            <template v-else>Create Account</template>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="showCropper" class="cropper-modal" data-modal>
        <div class="cropper-container">
            <div class="modal__header">
                <h2 class="modal__title">Crop Your Profile Image</h2>
                <button class="modal__close" @click="cancelCropping">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div class="modal__body">
                <Cropper ref="cropperRef" :src="imageSource" @change="onCropperChange"
                    :stencil-component="CircleStencil" :stencil-props="{
                        aspectRatio: 1,
                        movable: true,
                        resizable: true,
                        handlers: {
                            eastNorth: true,
                            north: true,
                            westNorth: true,
                            west: true,
                            westSouth: true,
                            south: true,
                            eastSouth: true,
                            east: true
                        },
                        handlersClasses: {
                            default: 'handler-default'
                        },
                        lineWidth: 2
                    }" class="cropper" />
            </div>
            <div class="modal__footer">
                <button class="btn btn--secondary" @click="cancelCropping" data-testid="cancel-cropped-image-button">Cancel</button>
                <PrimaryButton @click="saveCroppedImage" dataTestId="save-cropped-image-button">Save</PrimaryButton>
            </div>
        </div>
    </div>

    <Toast />
</template>

<style scoped lang="postcss">
.profile-photo {
    @apply relative w-[100px] h-[100px];
}

.profile-photo__actions {
    @apply absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2;
}

.profile-photo__upload-btn,
.profile-photo__remove-btn {
    @apply flex items-center justify-center w-9 p-2 h-9 2xl:w-11 2xl:h-11 2xl:p-2.5 rounded-full bg-echoes-primary text-white shadow-lg cursor-pointer transition-colors;
    @apply hover:bg-echoes-primary/50;
}

.profile-photo__remove-btn {
    @apply bg-red-500 hover:bg-red-600 p-2 2xl:p-2.5;
}

.profile-image-preview {
    @apply w-[100px] h-[100px] rounded-full flex items-center justify-center relative overflow-hidden bg-echoes-background-black border-2 border-echoes-border-gray;
}

.profile-image-preview.has-image {
    @apply border-echoes-primary;
}

.preview-image {
    @apply w-full h-full object-cover;
}

.default-avatar {
    @apply w-12 h-12  text-white/80;
}

.cropper-modal {
    @apply fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
    isolation: isolate;
}

.cropper-container {
    @apply overflow-hidden bg-echoes-background-black border border-echoes-border-gray rounded-xl shadow-xl w-full max-h-[85vh] mt-10 flex flex-col;
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;
    max-width: 540px;
    @apply 2xl:max-w-3xl;
}

.modal__header {
    @apply sticky top-0 z-10 flex items-center justify-between p-4 2xl:p-6 border-b border-echoes-border-gray bg-echoes-background-black;
}

.modal__title {
    @apply text-xl 2xl:text-2xl font-semibold text-white;
}

.modal__close {
    @apply text-white/80 hover:text-white p-1 rounded-full transition-colors;
}

.modal__body {
    @apply p-4 2xl:p-6 overflow-y-auto;
    flex: 1;
}

.modal__footer {
    @apply sticky bottom-0 z-10 p-4 2xl:p-6 border-t border-echoes-border-gray flex items-center justify-end gap-3 bg-echoes-background-black;
}

.btn {
    @apply px-4 py-2 2xl:px-6 2xl:py-3 rounded-lg font-medium transition-colors;
}

.btn--secondary {
    @apply px-4 py-1.5 2xl:px-6 2xl:py-2.5 rounded-lg text-sm 2xl:text-base font-medium transition-colors dark:hover:border-echoes-primary/50 dark:hover:text-echoes-primary dark:bg-white/10 dark:text-white dark:border-white/20 hover:border-echoes-primary/30 hover:text-echoes-primary bg-white text-gray-700 border border-gray-200;
}

.cropper {
    @apply bg-zinc-900;
}

.handler-default {
    @apply w-3 h-3 2xl:w-4 2xl:h-4 bg-echoes-primary border border-white rounded-full;
}

.field-label-container {
    @apply flex items-center mb-2 w-fit gap-2;
}

.label-error {
    @apply text-xs 2xl:text-sm text-red-500 m-0;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.btn-image-action {
    @apply flex items-center justify-center py-1.5 px-3 2xl:py-2 2xl:px-4 rounded-lg text-white text-xs 2xl:text-sm font-medium shadow-md cursor-pointer transition-colors;
}

.visually-hidden {
    @apply sr-only;
}
</style>
